import { useState, useCallback, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { ArrowLeft, ArrowRight, Check, AlertTriangle, User, MapPin, Mail, ClipboardList, Sparkles, X, Search } from 'lucide-react'
import Spinner from '@/components/Spinner'
import type { DublettenErgebnis, DublettenTreffer, PersonCreateData, AnschriftCreateData, KommunikationCreateData, TeilnehmerAnlegenPayload } from '@/types'

/* ── Konstanten ──────────────────────────────────────────── */

const GESCHLECHT_OPTIONS = [
  { value: '', label: '– Bitte wählen –' },
  { value: 'M', label: 'Männlich' },
  { value: 'W', label: 'Weiblich' },
  { value: 'D', label: 'Divers' },
  { value: '-', label: 'Keine Angabe' },
]

const ANREDE_OPTIONS = [
  { value: '', label: '– Keine –' },
  { value: 'Herr', label: 'Herr' },
  { value: 'Frau', label: 'Frau' },
]

/** KommunikationsTypFIDs – bekannt aus der TN-API */
const KOMM_TYPEN = [
  { fid: 10, label: 'E-Mail (privat)' },
  { fid: 11, label: 'E-Mail (geschäftlich)' },
  { fid: 12, label: 'E-Mail (Hochschule)' },
  { fid: 20, label: 'Telefon (privat)' },
  { fid: 21, label: 'Telefon (geschäftlich)' },
  { fid: 30, label: 'Mobil (privat)' },
  { fid: 31, label: 'Mobil (geschäftlich)' },
]

const STEPS = [
  { id: 'person', label: 'Persönliche Daten', icon: User },
  { id: 'adresse', label: 'Adresse', icon: MapPin },
  { id: 'kontakt', label: 'Kontaktdaten', icon: Mail },
  { id: 'zusammenfassung', label: 'Zusammenfassung', icon: ClipboardList },
]

/* ── Shared Styles ───────────────────────────────────────── */

const glassCard = 'bg-gradient-to-br from-white/85 via-white/80 to-fom-50/20 backdrop-blur-xl border border-white/60 ring-1 ring-white/70 rounded-xl shadow-sm shadow-fom-500/5'
const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-[0.82rem] placeholder:text-gray-400 focus:border-fom-400 focus:ring-1 focus:ring-fom-400 outline-none bg-white/70 transition-colors'
const labelCls = 'block text-[0.72rem] font-semibold text-gray-500 uppercase tracking-wider mb-1'
const requiredStar = <span className="text-red-400 ml-0.5">*</span>

/* ── Konfidenz-Badge ─────────────────────────────────────── */

function KonfidenzBadge({ konfidenz }: { konfidenz: string }) {
  const colors = konfidenz === 'HOCH'
    ? 'bg-red-50 text-red-700 border-red-200'
    : konfidenz === 'MITTEL'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-gray-50 text-gray-600 border-gray-200'
  return (
    <span className={`text-[0.65rem] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${colors}`}>
      {konfidenz}
    </span>
  )
}

/* ── Wizard ───────────────────────────────────────────────── */

export default function TeilnehmerAnlegen() {
  const { accountId } = useParams<{ accountId: string }>()

  // Firma-Info laden für Header
  const { data: firmaData } = useQuery({
    queryKey: ['firma', accountId],
    queryFn: () => api.firma(accountId!),
    enabled: !!accountId,
  })

  const [step, setStep] = useState(0)

  // ── Form-State ──────────────────────────────────────────
  const [person, setPerson] = useState<PersonCreateData>({
    nachname: '',
    vorname: '',
    geschlecht: '',
    anrede: '',
    titel_de: '',
    geburtsdatum: '',
    geburtsort: '',
    geburtsland: 'DE',
    staatsangehoerigkeit: 'DE',
    geburtsname: '',
  })

  const [anschrift, setAnschrift] = useState<AnschriftCreateData>({
    anschriften_typ: 'Person',
    verwendung: 'Anschrift privat',
    strasse: '',
    haus_nr: '',
    plz: '',
    stadt: '',
    bundesland: '',
    staat: 'DE',
  })

  const [kommunikation, setKommunikation] = useState<KommunikationCreateData[]>([
    { kommunikations_typ_fid: 10, name: '', value: '' },
    { kommunikations_typ_fid: 30, name: '', value: '' },
  ])

  // ── Dubletten ───────────────────────────────────────────
  const [dublettenResult, setDublettenResult] = useState<DublettenErgebnis | null>(null)
  const [dublettenChecked, setDublettenChecked] = useState(false)
  const [dublettenIgnored, setDublettenIgnored] = useState(false)

  const dublettenMutation = useMutation({
    mutationFn: () => {
      const email = kommunikation.find(k => [10, 11, 12].includes(k.kommunikations_typ_fid) && k.value.trim())?.value
      return api.dublettenCheck(accountId!, {
        nachname: person.nachname,
        vorname: person.vorname,
        geburtsdatum: person.geburtsdatum || undefined,
        geburtsname: person.geburtsname || undefined,
        email: email || undefined,
      })
    },
    onSuccess: (data) => {
      setDublettenResult(data)
      setDublettenChecked(true)
    },
  })

  // ── Anlegen ─────────────────────────────────────────────
  const [createSuccess, setCreateSuccess] = useState<Record<string, unknown> | null>(null)

  const createMutation = useMutation({
    mutationFn: () => {
      // Leere Strings → null für die TN-API
      const cleanObj = <T extends Record<string, unknown>>(obj: T): T => {
        const cleaned = { ...obj }
        for (const key of Object.keys(cleaned)) {
          if (cleaned[key] === '') (cleaned as Record<string, unknown>)[key] = null
        }
        return cleaned
      }
      const cleanedPerson = cleanObj({ ...person })
      // Geschlecht: leerer String → Default '-'
      if (!cleanedPerson.geschlecht) cleanedPerson.geschlecht = '-'
      const payload: TeilnehmerAnlegenPayload = {
        person: {
          ...cleanedPerson,
          dubletten_ignorieren: dublettenIgnored || dublettenResult?.status === 'KEINE_DUBLETTE',
        },
        anschriften: hasAnschrift() ? [cleanObj({ ...anschrift })] : [],
        kommunikation: kommunikation.filter(k => k.value.trim()),
        studiengaenge: [],
        zusatzstudenten: null as unknown as Record<string, unknown>,
        zusatzdaten: null as unknown as Record<string, unknown>,
      }
      return api.createTeilnehmer(accountId!, payload)
    },
    onSuccess: (data) => {
      setCreateSuccess(data)
      setStep(4) // Erfolg
    },
  })

  // ── Helpers ─────────────────────────────────────────────

  const hasAnschrift = useCallback(() => {
    return !!(anschrift.strasse?.trim() || anschrift.plz?.trim() || anschrift.stadt?.trim())
  }, [anschrift])

  const personValid = person.nachname.trim().length >= 2 && person.vorname.trim().length >= 2

  const updatePerson = (field: keyof PersonCreateData, value: string) =>
    setPerson(prev => ({ ...prev, [field]: value }))

  const updateAnschrift = (field: keyof AnschriftCreateData, value: string) =>
    setAnschrift(prev => ({ ...prev, [field]: value }))

  const updateKomm = (index: number, field: keyof KommunikationCreateData, value: string | number) =>
    setKommunikation(prev => prev.map((k, i) => i === index ? { ...k, [field]: value } : k))

  const addKomm = () =>
    setKommunikation(prev => [...prev, { kommunikations_typ_fid: 10, name: '', value: '' }])

  const removeKomm = (index: number) =>
    setKommunikation(prev => prev.filter((_, i) => i !== index))

  // Wenn Name sich ändert → Dubletten-Check zurücksetzen
  useEffect(() => {
    setDublettenChecked(false)
    setDublettenResult(null)
    setDublettenIgnored(false)
  }, [person.nachname, person.vorname, person.geburtsdatum])

  /* ── Navigation ──────────────────────────────────────────── */

  const canNext = () => {
    if (step === 0) return personValid
    if (step === 1) return true // Adresse optional
    if (step === 2) return true // Kontakt optional
    return true
  }

  const handleNext = () => {
    if (step < 3) setStep(step + 1)
  }

  const handleBack = () => {
    if (step > 0) setStep(step - 1)
  }

  const handleSubmit = () => {
    if (!dublettenChecked) {
      dublettenMutation.mutate()
      return
    }
    if (dublettenResult?.status === 'DUBLETTE_GEFUNDEN' && !dublettenIgnored) {
      return // Muss erst bestätigt werden
    }
    createMutation.mutate()
  }

  /* ── Step: Persönliche Daten ─────────────────────────────── */

  const renderStepPerson = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Vorname {requiredStar}</label>
          <input className={inputCls} value={person.vorname} onChange={e => updatePerson('vorname', e.target.value)} placeholder="Max" autoFocus />
        </div>
        <div>
          <label className={labelCls}>Nachname {requiredStar}</label>
          <input className={inputCls} value={person.nachname} onChange={e => updatePerson('nachname', e.target.value)} placeholder="Mustermann" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Geschlecht</label>
          <select className={inputCls} value={person.geschlecht} onChange={e => updatePerson('geschlecht', e.target.value)}>
            {GESCHLECHT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Anrede</label>
          <select className={inputCls} value={person.anrede} onChange={e => updatePerson('anrede', e.target.value)}>
            {ANREDE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Titel</label>
          <input className={inputCls} value={person.titel_de} onChange={e => updatePerson('titel_de', e.target.value)} placeholder="Dr., Prof." />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Geburtsdatum</label>
          <input type="date" className={inputCls} value={person.geburtsdatum} onChange={e => updatePerson('geburtsdatum', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Geburtsort</label>
          <input className={inputCls} value={person.geburtsort} onChange={e => updatePerson('geburtsort', e.target.value)} placeholder="Düsseldorf" />
        </div>
        <div>
          <label className={labelCls}>Geburtsland</label>
          <input className={inputCls} value={person.geburtsland} onChange={e => updatePerson('geburtsland', e.target.value)} placeholder="DE" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Staatsangehörigkeit</label>
          <input className={inputCls} value={person.staatsangehoerigkeit} onChange={e => updatePerson('staatsangehoerigkeit', e.target.value)} placeholder="DE" />
        </div>
        <div>
          <label className={labelCls}>Geburtsname</label>
          <input className={inputCls} value={person.geburtsname} onChange={e => updatePerson('geburtsname', e.target.value)} placeholder="falls abweichend" />
        </div>
      </div>
    </div>
  )

  /* ── Step: Adresse ───────────────────────────────────────── */

  const renderStepAdresse = () => (
    <div className="space-y-5">
      <p className="text-[0.78rem] text-gray-500 -mt-1">
        Wohnadresse des Teilnehmers <span className="text-gray-400">(optional)</span>
      </p>

      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-3">
          <label className={labelCls}>Straße</label>
          <input className={inputCls} value={anschrift.strasse} onChange={e => updateAnschrift('strasse', e.target.value)} placeholder="Musterstraße" />
        </div>
        <div>
          <label className={labelCls}>Hausnr.</label>
          <input className={inputCls} value={anschrift.haus_nr} onChange={e => updateAnschrift('haus_nr', e.target.value)} placeholder="42a" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>PLZ</label>
          <input className={inputCls} value={anschrift.plz} onChange={e => updateAnschrift('plz', e.target.value)} placeholder="40210" maxLength={10} />
        </div>
        <div>
          <label className={labelCls}>Stadt</label>
          <input className={inputCls} value={anschrift.stadt} onChange={e => updateAnschrift('stadt', e.target.value)} placeholder="Düsseldorf" />
        </div>
        <div>
          <label className={labelCls}>Bundesland</label>
          <input className={inputCls} value={anschrift.bundesland} onChange={e => updateAnschrift('bundesland', e.target.value)} placeholder="NRW" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Land</label>
          <input className={inputCls} value={anschrift.staat} onChange={e => updateAnschrift('staat', e.target.value)} placeholder="DE" />
        </div>
      </div>
    </div>
  )

  /* ── Step: Kontaktdaten ──────────────────────────────────── */

  const renderStepKontakt = () => (
    <div className="space-y-5">
      <p className="text-[0.78rem] text-gray-500 -mt-1">
        E-Mail-Adressen und Telefonnummern <span className="text-gray-400">(optional, aber empfohlen für Dublettenprüfung)</span>
      </p>

      {kommunikation.map((k, i) => (
        <div key={i} className="flex items-end gap-3">
          <div className="w-48 shrink-0">
            <label className={labelCls}>Typ</label>
            <select className={inputCls} value={k.kommunikations_typ_fid} onChange={e => updateKomm(i, 'kommunikations_typ_fid', Number(e.target.value))}>
              {KOMM_TYPEN.map(t => <option key={t.fid} value={t.fid}>{t.label}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className={labelCls}>Wert</label>
            <input
              className={inputCls}
              value={k.value}
              onChange={e => updateKomm(i, 'value', e.target.value)}
              placeholder={k.kommunikations_typ_fid < 20 ? 'email@beispiel.de' : '+49 170 1234567'}
            />
          </div>
          {kommunikation.length > 1 && (
            <button
              type="button"
              onClick={() => removeKomm(i)}
              className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors border-none bg-transparent cursor-pointer shrink-0"
            >
              <X size={16} />
            </button>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addKomm}
        className="text-[0.78rem] text-fom-600 hover:text-fom-700 font-medium border-none bg-transparent cursor-pointer px-0"
      >
        + Weiteren Kontakt hinzufügen
      </button>
    </div>
  )

  /* ── Step: Zusammenfassung ───────────────────────────────── */

  const renderStepZusammenfassung = () => {
    return (
      <div className="space-y-6">
        {/* Zusammenfassungs-Karten */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Person */}
          <div className="bg-white/60 rounded-lg border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <User size={14} className="text-fom-600" />
              <h4 className="text-[0.75rem] font-semibold text-gray-700 uppercase tracking-wider">Person</h4>
              <button onClick={() => setStep(0)} className="ml-auto text-[0.68rem] text-fom-600 hover:text-fom-700 border-none bg-transparent cursor-pointer">Ändern</button>
            </div>
            <div className="space-y-1 text-[0.8rem]">
              <p className="font-semibold text-gray-900">
                {person.anrede && `${person.anrede} `}
                {person.titel_de && `${person.titel_de} `}
                {person.vorname} {person.nachname}
              </p>
              {person.geschlecht && <p className="text-gray-500">Geschlecht: {GESCHLECHT_OPTIONS.find(o => o.value === person.geschlecht)?.label}</p>}
              {person.geburtsdatum && <p className="text-gray-500">Geb.: {new Date(person.geburtsdatum).toLocaleDateString('de-DE')}</p>}
              {person.geburtsort && <p className="text-gray-500">Geburtsort: {person.geburtsort}</p>}
              {person.geburtsname && <p className="text-gray-500">Geburtsname: {person.geburtsname}</p>}
            </div>
          </div>

          {/* Adresse */}
          <div className="bg-white/60 rounded-lg border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={14} className="text-fom-600" />
              <h4 className="text-[0.75rem] font-semibold text-gray-700 uppercase tracking-wider">Adresse</h4>
              <button onClick={() => setStep(1)} className="ml-auto text-[0.68rem] text-fom-600 hover:text-fom-700 border-none bg-transparent cursor-pointer">Ändern</button>
            </div>
            {hasAnschrift() ? (
              <div className="space-y-1 text-[0.8rem]">
                <p className="text-gray-900">{anschrift.strasse} {anschrift.haus_nr}</p>
                <p className="text-gray-900">{anschrift.plz} {anschrift.stadt}</p>
                {anschrift.bundesland && <p className="text-gray-500">{anschrift.bundesland}</p>}
                {anschrift.staat && <p className="text-gray-500">{anschrift.staat}</p>}
              </div>
            ) : (
              <p className="text-[0.8rem] text-gray-400 italic">Keine Adresse angegeben</p>
            )}
          </div>

          {/* Kontakt */}
          <div className="bg-white/60 rounded-lg border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail size={14} className="text-fom-600" />
              <h4 className="text-[0.75rem] font-semibold text-gray-700 uppercase tracking-wider">Kontakt</h4>
              <button onClick={() => setStep(2)} className="ml-auto text-[0.68rem] text-fom-600 hover:text-fom-700 border-none bg-transparent cursor-pointer">Ändern</button>
            </div>
            {kommunikation.filter(k => k.value.trim()).length > 0 ? (
              <div className="space-y-1 text-[0.8rem]">
                {kommunikation.filter(k => k.value.trim()).map((k, i) => (
                  <p key={i} className="text-gray-900">
                    <span className="text-gray-500 text-[0.72rem]">{KOMM_TYPEN.find(t => t.fid === k.kommunikations_typ_fid)?.label}: </span>
                    {k.value}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-[0.8rem] text-gray-400 italic">Keine Kontaktdaten</p>
            )}
          </div>
        </div>

        {/* Dublettenprüfung */}
        <div className="border-t border-gray-200 pt-5">
          <h4 className="text-[0.8rem] font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Search size={14} />
            Dublettenprüfung
          </h4>

          {!dublettenChecked && !dublettenMutation.isPending && (
            <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-4">
              <p className="text-[0.8rem] text-blue-700 mb-3">
                Vor dem Anlegen wird geprüft, ob die Person bereits im System existiert.
              </p>
              <button
                type="button"
                onClick={() => dublettenMutation.mutate()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-[0.8rem] font-medium hover:bg-blue-700 transition-colors border-none cursor-pointer"
              >
                <Search size={14} />
                Jetzt prüfen
              </button>
            </div>
          )}

          {dublettenMutation.isPending && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Spinner className="!mt-0" />
              <span className="text-[0.8rem] text-gray-600">Prüfe auf vorhandene Einträge…</span>
            </div>
          )}

          {dublettenMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-[0.8rem] text-red-700">
                Fehler bei der Dublettenprüfung: {dublettenMutation.error instanceof Error ? dublettenMutation.error.message : 'Unbekannter Fehler'}
              </p>
              <button
                type="button"
                onClick={() => dublettenMutation.mutate()}
                className="mt-2 text-[0.78rem] text-red-600 hover:text-red-800 font-medium border-none bg-transparent cursor-pointer px-0"
              >
                Erneut versuchen
              </button>
            </div>
          )}

          {dublettenChecked && dublettenResult?.status === 'KEINE_DUBLETTE' && (
            <div className="bg-green-50/60 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                <Check size={16} className="text-green-600" />
              </div>
              <div>
                <p className="text-[0.82rem] text-green-800 font-semibold">Keine Dubletten gefunden</p>
                <p className="text-[0.75rem] text-green-600">Die Person kann angelegt werden.</p>
              </div>
            </div>
          )}

          {dublettenChecked && dublettenResult?.status === 'DUBLETTE_GEFUNDEN' && (
            <div className="space-y-3">
              <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-[0.82rem] text-amber-800 font-semibold">
                      Mögliche Dublette{dublettenResult.treffer.length > 1 ? 'n' : ''} gefunden!
                    </p>
                    <p className="text-[0.75rem] text-amber-600 mt-0.5">
                      {dublettenResult.hinweis || `${dublettenResult.treffer.length} möglicherweise übereinstimmende Person(en) im System`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Treffer-Liste */}
              <div className="space-y-2">
                {dublettenResult.treffer.map((t: DublettenTreffer, i: number) => (
                  <div key={i} className="bg-white/80 border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-[0.82rem] font-semibold text-gray-900">
                        {t.vorname} {t.nachname}
                        <span className="text-gray-400 font-normal ml-2">#{t.personen_id}</span>
                      </p>
                      {t.geburtsdatum && (
                        <p className="text-[0.72rem] text-gray-500">
                          Geb.: {new Date(t.geburtsdatum).toLocaleDateString('de-DE')}
                        </p>
                      )}
                      <p className="text-[0.72rem] text-gray-500 mt-0.5">{t.match_beschreibung}</p>
                    </div>
                    <KonfidenzBadge konfidenz={t.konfidenz} />
                  </div>
                ))}
              </div>

              {/* Trotzdem anlegen */}
              {!dublettenIgnored ? (
                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setDublettenIgnored(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-100 text-amber-800 text-[0.78rem] font-medium hover:bg-amber-200 transition-colors border border-amber-300 cursor-pointer"
                  >
                    <AlertTriangle size={14} />
                    Trotzdem anlegen
                  </button>
                  <span className="text-[0.72rem] text-gray-400">Die Person ist mir bekannt – kein Duplikat.</span>
                </div>
              ) : (
                <div className="bg-green-50/60 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                  <Check size={16} className="text-green-600" />
                  <span className="text-[0.8rem] text-green-700 font-medium">Dubletten-Warnung ignoriert – Person kann angelegt werden.</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  /* ── Step: Erfolg ────────────────────────────────────────── */

  const renderStepErfolg = () => {
    const newId = (createSuccess?.personen_id ?? (createSuccess?.person as Record<string, unknown>)?.personen_id) as number | string | undefined
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <Sparkles size={28} className="text-green-600" />
        </div>
        <h3 className="text-[1.15rem] font-bold text-gray-900 mb-2">Teilnehmer erfolgreich angelegt!</h3>
        <p className="text-[0.85rem] text-gray-500 mb-1">
          <strong>{person.vorname} {person.nachname}</strong> wurde im System erstellt.
        </p>
        {newId && (
          <p className="text-[0.78rem] text-gray-400 mb-6">PersonenID: <strong className="text-gray-600">{String(newId)}</strong></p>
        )}
        <div className="flex items-center justify-center gap-3">
          {newId && (
            <Link
              to={`/firma/${accountId}/teilnehmer/${newId}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-fom-600 text-white text-[0.82rem] font-medium hover:bg-fom-700 transition-colors no-underline"
            >
              <User size={15} />
              Profil öffnen
            </Link>
          )}
          <Link
            to={`/firma/${accountId}#teilnehmer`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white border border-gray-200 text-gray-700 text-[0.82rem] font-medium hover:bg-gray-50 transition-colors no-underline"
          >
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    )
  }

  /* ── Render ──────────────────────────────────────────────── */

  const isErfolg = step === 4

  return (
    <div className="px-6 py-6 max-w-4xl mx-auto">
      {/* Header mit Zurück */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to={`/firma/${accountId}#teilnehmer`}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white/50 backdrop-blur border border-white/60 text-[0.8rem] font-medium text-gray-600 hover:text-fom-700 hover:bg-fom-50/50 hover:border-fom-200 transition-all no-underline shadow-sm"
        >
          <ArrowLeft size={15} />
          Zurück
        </Link>
        <div>
          <h1 className="text-[1.25rem] font-bold text-gray-900 leading-tight">Teilnehmer anlegen</h1>
          {firmaData?.firma?.name && (
            <p className="text-[0.75rem] text-gray-500">{firmaData.firma.name}</p>
          )}
        </div>
      </div>

      {/* Step Indicator */}
      {!isErfolg && (
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon
            const isActive = i === step
            const isDone = i < step
            return (
              <div key={s.id} className="flex items-center gap-2 flex-1">
                <button
                  type="button"
                  onClick={() => i < step && setStep(i)}
                  disabled={i > step}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[0.75rem] font-medium transition-all border-none cursor-pointer w-full ${
                    isActive
                      ? 'bg-fom-600 text-white shadow-md shadow-fom-500/20'
                      : isDone
                        ? 'bg-fom-50 text-fom-700 hover:bg-fom-100'
                        : 'bg-white/50 text-gray-400 cursor-default'
                  }`}
                >
                  {isDone ? (
                    <div className="w-5 h-5 rounded-full bg-fom-500 text-white flex items-center justify-center shrink-0">
                      <Check size={12} />
                    </div>
                  ) : (
                    <Icon size={15} className="shrink-0" />
                  )}
                  <span className="truncate hidden sm:inline">{s.label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`w-4 h-px shrink-0 ${i < step ? 'bg-fom-300' : 'bg-gray-200'}`} />
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Form Card */}
      <div className={`${glassCard} p-6`}>
        {step === 0 && renderStepPerson()}
        {step === 1 && renderStepAdresse()}
        {step === 2 && renderStepKontakt()}
        {step === 3 && renderStepZusammenfassung()}
        {step === 4 && renderStepErfolg()}

        {/* Navigation */}
        {!isErfolg && (
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-gray-200/60">
            <button
              type="button"
              onClick={handleBack}
              disabled={step === 0}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[0.8rem] font-medium transition-colors border-none cursor-pointer ${
                step === 0
                  ? 'text-gray-300 bg-transparent cursor-default'
                  : 'text-gray-600 bg-white/60 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <ArrowLeft size={15} />
              Zurück
            </button>

            {step < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canNext()}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[0.82rem] font-medium transition-all border-none cursor-pointer ${
                  canNext()
                    ? 'bg-fom-600 text-white hover:bg-fom-700 shadow-md shadow-fom-500/20'
                    : 'bg-gray-200 text-gray-400 cursor-default'
                }`}
              >
                Weiter
                <ArrowRight size={15} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={createMutation.isPending || (dublettenChecked && dublettenResult?.status === 'DUBLETTE_GEFUNDEN' && !dublettenIgnored)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[0.82rem] font-medium transition-all border-none cursor-pointer ${
                  dublettenChecked && (dublettenResult?.status === 'KEINE_DUBLETTE' || dublettenIgnored)
                    ? 'bg-green-600 text-white hover:bg-green-700 shadow-md shadow-green-500/20'
                    : !dublettenChecked
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-500/20'
                      : 'bg-gray-200 text-gray-400 cursor-default'
                }`}
              >
                {createMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Wird angelegt…
                  </>
                ) : !dublettenChecked ? (
                  <>
                    <Search size={15} />
                    Dublettenprüfung starten
                  </>
                ) : (
                  <>
                    <Check size={15} />
                    Teilnehmer anlegen
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {createMutation.isError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-[0.8rem] text-red-700">
              Fehler beim Anlegen: {createMutation.error instanceof Error ? createMutation.error.message : 'Unbekannter Fehler'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
